import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { pick } from 'ramda';
import { throttle } from 'underscore';
import { useKeepAlive } from 'server';
import { useUserSelector } from 'js/store/hooks';

import * as actions from '../../store/actions';
import * as constants from './constants';

export const useUserKeepAlive = () => {
    const keepAlive = useKeepAlive();
    const dispatch = useDispatch();
    const { isLoggedIn, isAlive } = useUserSelector(pick(['isLoggedIn', 'isAlive']));
    const keepAliveInterval = useRef<ReturnType<typeof setInterval>>();
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const [isPageHidden, setIsPageHidden] = useState(false);
    const [isNoActivity, setIsNoActivity] = useState(false);

    // hook only responsible for setting isPageHidden
    useEffect(() => {
        const handler = () => setIsPageHidden(document.hidden);

        document.addEventListener('visibilitychange', handler);

        return () => {
            document.removeEventListener('visibilitychange', handler);
        };
    }, []);

    // hook only responsible for setting isNoActivity
    useEffect(() => {
        const createTimeout = () => timer.current = setTimeout(() => setIsNoActivity(true), constants.NO_ACTIVITY_TIMEOUT_MS);
        const handler = throttle(() => {
            clearTimeout(timer.current);
            createTimeout();
            setIsNoActivity(false);
        }, constants.NO_ACTIVITY_THROTTLE_MS);

        createTimeout();

        document.addEventListener('mousemove', handler);
        document.addEventListener('keydown', handler);

        return () => {
            document.removeEventListener('mousemove', handler);
            document.removeEventListener('keydown', handler);
        };
    }, []);

    // hook only responsible for setting isAlive
    useEffect(() => {
        dispatch(actions.setIsAlive(!isNoActivity && !isPageHidden));
    }, [
        dispatch,
        isPageHidden,
        isNoActivity
    ]);

    // hook only responsible for reporting to IMS
    useEffect(() => {
        if (!isAlive || !isLoggedIn) {
            // if player has left, or if is not logged in -- do nothing
            return;
        }

        keepAliveInterval.current = setInterval(() => keepAlive(), constants.IMS_KEEP_ALIVE_INTERVAL_MS);

        return () => {
            clearInterval(keepAliveInterval.current);
        };
    }, [
        isAlive,
        keepAlive,
        isLoggedIn,
        dispatch
    ]);
};
